import posthog from 'posthog-js';

const APP_NAME = 'client';
const REDACTED = '[redacted]';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const POSTHOG_TOKEN = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN || '';
const POSTHOG_PROJECT_ID = import.meta.env.VITE_POSTHOG_PROJECT_ID || '';
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE || null;
const SENSITIVE_CAPTURE_CLASS = 'ph-no-capture';
const PASSWORD_MASK_CLASS = 'ph-mask';
const SENSITIVE_FIELD_PATTERN =
    /\b(ssn|social[-_\s]*security|payment|depositlink|stripe|card[-_\s]*number|credit[-_\s]*card|cvv|cvc|expiry|expiration|routing[-_\s]*number|bank[-_\s]*account|account[-_\s]*number)\b/i;
const SENSITIVE_PROPERTY_PATTERN =
    /(password|ssn|social_security|socialSecurity|card_number|credit_card|cvv|cvc|routing_number|bank_account|account_number|client_secret|secret|dateOfBirth|date_of_birth|dob)/i;
const FIELD_SELECTOR = 'input, textarea, select, iframe, [contenteditable="true"]';

let sensitiveGuardInstalled = false;

export const isPostHogEnabled = Boolean(POSTHOG_TOKEN && import.meta.env.MODE === 'production');

export const posthogClient = posthog;

const baseProperties = () => ({
    app: APP_NAME,
    environment: ENVIRONMENT,
    release: RELEASE,
    posthog_project_id: POSTHOG_PROJECT_ID || undefined,
    route: window.location.pathname,
});

function sanitizeValue(value) {
    if (Array.isArray(value)) return value.map(sanitizeValue);
    if (!value || typeof value !== 'object') return value;

    return Object.entries(value).reduce((next, [key, nestedValue]) => {
        next[key] = SENSITIVE_PROPERTY_PATTERN.test(key) ? REDACTED : sanitizeValue(nestedValue);
        return next;
    }, {});
}

function sanitizeProperties(properties = {}) {
    return Object.entries(properties).reduce((next, [key, value]) => {
        next[key] = SENSITIVE_PROPERTY_PATTERN.test(key) ? REDACTED : sanitizeValue(value);
        return next;
    }, {});
}

function beforeSend(event) {
    if (!event?.properties) return event;

    return {
        ...event,
        properties: {
            ...sanitizeProperties(event.properties),
            ...baseProperties(),
        },
    };
}

function escapeCssIdentifier(value) {
    if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
}

function descriptorForElement(element) {
    const values = [
        element.getAttribute('name'),
        element.getAttribute('id'),
        element.getAttribute('type'),
        element.getAttribute('autocomplete'),
        element.getAttribute('aria-label'),
        element.getAttribute('placeholder'),
        element.getAttribute('data-testid'),
        element.getAttribute('class'),
    ];

    if (element.id) {
        const label = document.querySelector(`label[for="${escapeCssIdentifier(element.id)}"]`);
        values.push(label?.textContent);
    }

    return values.filter(Boolean).join(' ');
}

function annotateSensitiveFields(root = document) {
    const fields = [
        ...(root instanceof Element && root.matches(FIELD_SELECTOR) ? [root] : []),
        ...root.querySelectorAll(FIELD_SELECTOR),
    ];

    fields.forEach((element) => {
        const descriptor = descriptorForElement(element);
        const type = element.getAttribute('type') || '';

        if (type.toLowerCase() === 'password' || /password/i.test(descriptor)) {
            element.classList.add(PASSWORD_MASK_CLASS);
            return;
        }

        if (SENSITIVE_FIELD_PATTERN.test(descriptor)) {
            element.classList.add(SENSITIVE_CAPTURE_CLASS);
            element.setAttribute('data-posthog-sensitive', 'true');
        }
    });
}

export function installPostHogSensitiveFieldGuard() {
    if (!isPostHogEnabled || sensitiveGuardInstalled || typeof document === 'undefined') return;

    sensitiveGuardInstalled = true;
    annotateSensitiveFields();

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType !== Node.ELEMENT_NODE) return;
                annotateSensitiveFields(node);
            });
        });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
}

export function initPostHog() {
    if (!isPostHogEnabled) return;

    installPostHogSensitiveFieldGuard();
    posthog.init(POSTHOG_TOKEN, {
        api_host: POSTHOG_HOST,
        autocapture: true,
        capture_pageview: 'history_change',
        disable_session_recording: false,
        mask_all_element_attributes: true,
        mask_all_text: false,
        session_recording: {
            maskAllInputs: false,
            maskTextSelector: `.${PASSWORD_MASK_CLASS}`,
            blockSelector: `.${SENSITIVE_CAPTURE_CLASS}`,
            maskInputOptions: { password: true },
        },
        before_send: beforeSend,
        loaded: (client) => {
            client.register(baseProperties());
        },
    });
}

export function syncPostHogRoute(
    pathname = window.location.pathname,
    search = window.location.search
) {
    if (!isPostHogEnabled) return;

    posthog.register({
        ...baseProperties(),
        route: pathname,
        route_search: search || undefined,
    });
}

export function identifyPostHogUser(distinctId, properties = {}) {
    if (!isPostHogEnabled || !distinctId) return;

    const safeProperties = sanitizeProperties({
        ...baseProperties(),
        ...properties,
    });

    posthog.identify(String(distinctId), safeProperties);
}

export function groupPostHogClient(clientId, properties = {}) {
    if (!isPostHogEnabled || !clientId) return;

    posthog.group('client', String(clientId), sanitizeProperties(properties));
}

export function capturePostHogEvent(eventName, properties = {}) {
    if (!isPostHogEnabled || !eventName) return;

    posthog.capture(eventName, sanitizeProperties({ ...baseProperties(), ...properties }));
}

export function resetPostHog() {
    if (!isPostHogEnabled) return;

    posthog.reset();
    posthog.register(baseProperties());
}
